import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export interface PresencePeer {
  user_id: string;
  name: string;
  color: string;
  tab: string | null;
}

export interface ActivityMessage {
  project_id: string;
  actor_id: string;
  actor_name: string;
  verb: string;
  entity_type: string;
  entity_id: string | null;
  entity_label: string | null;
  summary: string | null;
  created_at: string;
}

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;
const PING_INTERVAL_MS = 25000;

const VERB_LABELS: Record<string, string> = {
  created: "created",
  updated: "updated",
  commented: "commented on",
  comment_edited: "edited a comment on",
  comment_deleted: "deleted a comment on",
  resolved: "resolved a comment on",
  reopened: "reopened a comment on",
  invited: "invited",
  role_changed: "changed a role for",
  removed: "removed",
  left: "left the project",
};

function describeActivity(e: ActivityMessage): string {
  if (e.verb === "left") return `${e.actor_name || "Someone"} left the project`;
  const verb = VERB_LABELS[e.verb] || e.verb;
  const label = e.entity_label ? `"${e.entity_label}"` : e.entity_type;
  return `${e.actor_name || "Someone"} ${verb} ${label}`;
}

/**
 * Presence + live-refresh (not concurrent editing) for a project: shows who else is
 * viewing right now, and pops a toast + calls onActivity() when a collaborator saves
 * a change elsewhere. In-memory single-process backend (see realtime.py) — a self-
 * triggered refetch is harmless, so no attempt is made to exclude the originating client.
 */
export function useProjectPresence(projectId: string | undefined, onActivity?: (event: ActivityMessage) => void) {
  const { token, user } = useAuth();
  const [peers, setPeers] = useState<PresencePeer[]>([]);
  const [connected, setConnected] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempt = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const manualClose = useRef(false);
  const currentTab = useRef<string | null>(null);
  const onActivityRef = useRef(onActivity);
  onActivityRef.current = onActivity;

  const connect = useCallback(() => {
    if (!projectId || !token) return;
    manualClose.current = false;

    const base = process.env.REACT_APP_BACKEND_URL || "";
    const wsBase = base.replace(/^http/, "ws");
    const ws = new WebSocket(`${wsBase}/api/ws/projects/${projectId}?token=${encodeURIComponent(token)}`);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      reconnectAttempt.current = 0;
      if (currentTab.current) {
        ws.send(JSON.stringify({ type: "tab", tab: currentTab.current }));
      }
      pingTimer.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "ping" }));
      }, PING_INTERVAL_MS);
    };

    ws.onmessage = (event) => {
      let msg: any;
      try {
        msg = JSON.parse(event.data);
      } catch {
        return;
      }
      if (msg.type === "presence") {
        setPeers((msg.peers || []).filter((p: PresencePeer) => p.user_id !== user?.id));
      } else if (msg.type === "activity") {
        const activityEvent: ActivityMessage = msg.event;
        if (activityEvent.actor_id !== user?.id) {
          toast(describeActivity(activityEvent));
        }
        onActivityRef.current?.(activityEvent);
      }
    };

    ws.onclose = () => {
      setConnected(false);
      if (pingTimer.current) clearInterval(pingTimer.current);
      if (manualClose.current) return;
      const delay = Math.min(RECONNECT_BASE_MS * 2 ** reconnectAttempt.current, RECONNECT_MAX_MS);
      reconnectAttempt.current += 1;
      reconnectTimer.current = setTimeout(connect, delay);
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [projectId, token, user?.id]);

  useEffect(() => {
    connect();
    return () => {
      manualClose.current = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (pingTimer.current) clearInterval(pingTimer.current);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [connect]);

  const setActiveTab = useCallback((tab: string) => {
    currentTab.current = tab;
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "tab", tab }));
    }
  }, []);

  return { peers, connected, setActiveTab };
}
